import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import {
  CollectionMembershipBadge,
  CollectionOriginBadge,
  getCollectionMembershipBadgeMeta,
  getCollectionMembershipBadgeToneClassName,
  getCollectionOriginBadgeMeta,
  getCollectionOriginBadgeToneClassName,
} from '@/components/collections/CollectionBadges'
import CreateActionButton from '@/components/patterns/create-action-button'
import ImageThumbnail from '@/components/media/ImageThumbnail'
import { PriceChangeAlert } from '@/components/products/price-changes/PriceChangeAlert'
import { InfoNoticeAction } from '@/components/patterns/info-notice'
import ProductCatalogNav from '@/components/products/ProductCatalogNav'
import {
  getIndexPaginationMeta,
  IndexDataTable,
  IndexTableClearFiltersButton,
  IndexTablePagination,
  IndexTableSearchBox,
  IndexTableToolbar,
} from '@/components/tables/IndexTable'
import { useIndexTableSearchParams } from '@/components/tables/useIndexTableSearchParams'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import useIsGlobalOrg from '@/hooks/useIsGlobalOrg'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can, useAbility } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import { OrganizationType } from '@/lib/enums/OrganizationType'
import { ProductStatus } from '@/lib/enums/ProductStatus'
import { ProductStorefrontVisibility } from '@/lib/enums/ProductStorefrontVisibility'
import queryClient from '@/lib/queryClient'
import { prefetchIndexTableRouteQueries } from '@/lib/services/index-table-query-options'
import { cn, getErrorMessage, QueryFilters } from '@/lib/utils'
import {
  collectionQueries,
  invalidateCollectionImportDependencies,
  useActiveGlobalCollectionImportAttemptQuery,
  useImportGlobalCollectionSourceMutation,
  useGlobalCollectionImportAttemptQuery,
  useCollectionsTable,
} from '@/lib/services/collections/collections.query'
import { useOrganization } from '@/lib/services/organizations/organizations.query'
import {
  priceAdjustmentFilters,
  priceAdjustmentQueries,
  supportsPriceAdjustmentsForOrganizationId,
  usePriceAdjustments,
} from '@/lib/services/price-adjustments/priceAdjustments.query'
import {
  getPriceAdjustmentActionLabel,
  getPriceAdjustmentSignedAmountLabel,
  getPriceAdjustmentScheduleSummary,
} from '@/lib/services/price-adjustments/priceAdjustments.summary'
import { isSubscriptionPackageCollection } from '@/lib/subscription-package-collection'
import {
  createFileRoute,
  Link,
  notFound,
  redirect,
  stripSearchParams,
  useNavigate,
  useParams,
} from '@tanstack/react-router'
import {
  CalendarClock,
  Check,
  ChevronRight,
  EyeOff,
  FileText,
  Import,
  LibraryBig,
  SlidersHorizontal,
  Store,
  Tags,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod/v4'

const collectionHeaderActionClassName = 'min-w-[11.75rem]'
const DEFAULT_PAGE_SIZE = 20
// Re-enable after the FSN global catalog has been imported and curated for florist self-service.
const ENABLE_FSN_CATALOG_IMPORT_ACTION = false

export const defaultCollectionListSearchParams = {
  type: 'product-collections',
  publish_status: 'all',
  collection_type: 'all',
  sort: 'name_asc',
  page: 1,
  page_size: DEFAULT_PAGE_SIZE,
}

export const strippedCollectionListSearchParams = {
  type: defaultCollectionListSearchParams.type,
  publish_status: defaultCollectionListSearchParams.publish_status,
  collection_type: defaultCollectionListSearchParams.collection_type,
  sort: defaultCollectionListSearchParams.sort,
  page: defaultCollectionListSearchParams.page,
  page_size: defaultCollectionListSearchParams.page_size,
}

const collectionTypeValues = ['product-collections', 'add-on-collections']
const collectionPublishStatusValues = ['all', 'published', 'draft']
const collectionPublishStatusOptions = [
  {
    value: 'all',
    label: 'All visibility',
    icon: <SlidersHorizontal className="size-4" />,
  },
  {
    value: 'published',
    label: 'Visible',
    icon: (
      <span className="inline-flex items-center" aria-hidden="true">
        <Check className="size-3.5" />
      </span>
    ),
  },
  {
    value: 'draft',
    label: 'Hidden',
    icon: (
      <span className="inline-flex items-center" aria-hidden="true">
        <EyeOff className="size-3.5" />
      </span>
    ),
  },
]

const collectionTypeFilterValues = ['all', 'built_in', 'custom']
const collectionTypeFilterOptions = [
  {
    value: 'all',
    label: 'All types',
    icon: <SlidersHorizontal className="size-4" />,
  },
  {
    value: 'built_in',
    label: 'Built-in',
  },
  {
    value: 'custom',
    label: 'Custom',
  },
]

const collectionSortValues = [
  'name_asc',
  'name_desc',
  'type_asc',
  'type_desc',
  'products_asc',
  'products_desc',
  'visible_products_asc',
  'visible_products_desc',
  'active_products_asc',
  'active_products_desc',
  'price_change_asc',
  'price_change_desc',
  'last_changed_asc',
  'last_changed_desc',
  'imported_shops_asc',
  'imported_shops_desc',
]

export const collectionListSearchSchema = z.object({
  search: z.string().optional().catch(undefined),
  type: z.enum(collectionTypeValues).catch(defaultCollectionListSearchParams.type),
  publish_status: z
    .enum(collectionPublishStatusValues)
    .catch(defaultCollectionListSearchParams.publish_status),
  collection_type: z
    .enum(collectionTypeFilterValues)
    .catch(defaultCollectionListSearchParams.collection_type),
  sort: z.enum(collectionSortValues).catch(defaultCollectionListSearchParams.sort),
  page: z.coerce.number().int().positive().catch(defaultCollectionListSearchParams.page),
  page_size: z.coerce
    .number()
    .int()
    .positive()
    .max(100)
    .catch(defaultCollectionListSearchParams.page_size),
})

const collectionIndexSortColumns = {
  collection: {
    asc: 'name_asc',
    desc: 'name_desc',
    defaultSort: 'name_asc',
  },
  products: {
    asc: 'products_asc',
    desc: 'products_desc',
    defaultSort: 'products_desc',
  },
  type: {
    asc: 'type_asc',
    desc: 'type_desc',
    defaultSort: 'type_asc',
  },
  visibleProducts: {
    asc: 'visible_products_asc',
    desc: 'visible_products_desc',
    defaultSort: 'visible_products_desc',
  },
  priceChange: {
    asc: 'price_change_asc',
    desc: 'price_change_desc',
    defaultSort: 'price_change_desc',
  },
  lastChanged: {
    asc: 'last_changed_asc',
    desc: 'last_changed_desc',
    defaultSort: 'last_changed_desc',
  },
  importedShops: {
    asc: 'imported_shops_asc',
    desc: 'imported_shops_desc',
    defaultSort: 'imported_shops_desc',
  },
}

function isGlobalOrganizationId(organizationId) {
  return Number(organizationId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)
}

export function getCollectionListSearchParams(search) {
  return collectionListSearchSchema.parse(search ?? {})
}

function collectionIndexFilters({ includeImportedShopsCount = false, search = {} } = {}) {
  const parsedSearch = getCollectionListSearchParams(search)
  const trimmedSearch = parsedSearch.search?.trim()
  const publishStatusFilter =
    parsedSearch.publish_status === 'published'
      ? true
      : parsedSearch.publish_status === 'draft'
        ? false
        : undefined
  const collectionOriginFilter =
    parsedSearch.collection_type === 'built_in'
      ? 'platform'
      : parsedSearch.collection_type === 'custom'
        ? 'custom'
        : undefined

  return new QueryFilters({ include: ['media'] })
    .applyFilter({
      ...(includeImportedShopsCount ? { with_imported_shops_count: true } : {}),
      ...(publishStatusFilter !== undefined ? { is_published: publishStatusFilter } : {}),
      ...(collectionOriginFilter ? { collection_origin: collectionOriginFilter } : {}),
      ...(trimmedSearch ? { search: trimmedSearch } : {}),
      sort: parsedSearch.sort,
      page: parsedSearch.page,
      page_size: parsedSearch.page_size,
      with_last_changed_at: true,
      with_products_count: true,
      with_visible_products_count: true,
    })
    .get()
}

function getCollectionName(collection) {
  return collection.name ?? ''
}

function getProductCount(collection) {
  return Number(collection?.products_count ?? collection?.products?.length ?? 0)
}

function productIsVisibleOnWebsiteInCollection(product, collection) {
  if (product?.status !== ProductStatus.Active.Value) {
    return false
  }

  const visibility = product.storefront_visibility ?? ProductStorefrontVisibility.Always.Value

  if (visibility === ProductStorefrontVisibility.Always.Value) {
    return true
  }

  if (visibility === ProductStorefrontVisibility.PublishedCollection.Value) {
    if (Array.isArray(product.collections)) {
      return product.collections.some((productCollection) => productCollection?.is_published)
    }

    return collection?.is_published === true
  }

  return false
}

function getVisibleProductCount(collection) {
  if (collection?.visible_products_count !== undefined) {
    return Number(collection.visible_products_count ?? 0)
  }

  if (Array.isArray(collection?.products)) {
    return collection.products.filter((product) =>
      productIsVisibleOnWebsiteInCollection(product, collection)
    ).length
  }

  return Number(collection?.active_products_count ?? 0)
}

function getImportedShopCount(collection) {
  return Number(collection?.imported_shops_count ?? 0)
}

function getLastChangedAt(collection) {
  return collection?.last_changed_at ?? collection?.updated_at ?? null
}

function formatLastChangedAt(value) {
  return value ? dayjs(value).format('MM/DD/YYYY') : 'No changes'
}

function getPriceAdjustmentStartTimestamp(adjustment) {
  const startsAt = adjustment?.active_window?.starts_at
  const timestamp = startsAt ? dayjs(startsAt).valueOf() : 0

  return Number.isFinite(timestamp) ? timestamp : 0
}

function compareWinningPriceAdjustments(a, b) {
  return (
    Number(a?.priority ?? 0) - Number(b?.priority ?? 0) ||
    getPriceAdjustmentStartTimestamp(a) - getPriceAdjustmentStartTimestamp(b) ||
    Number(a?.id ?? 0) - Number(b?.id ?? 0)
  )
}

function getActiveGlobalPriceChange(priceAdjustments = []) {
  return priceAdjustments
    .filter((adjustment) => adjustment?.status === 'active' && adjustment?.scope_type === 'global')
    .sort((a, b) => compareWinningPriceAdjustments(b, a))[0]
}

function getCollectionActivePriceAdjustment(collection) {
  if (isSubscriptionPackageCollection(collection)) {
    return null
  }

  return collection.active_price_adjustment ?? null
}

function getPriceAdjustmentBadgeMeta(adjustment) {
  if (adjustment?.scope_type === 'global') {
    return {
      scopeLabel: 'All products',
      variantClassName:
        'border-status-warning-border bg-status-warning-background text-status-warning-foreground hover:bg-status-warning-background dark:border-status-warning-border dark:bg-status-warning-background dark:text-status-warning-foreground',
    }
  }

  return {
    scopeLabel: 'Collection',
    variantClassName:
      'border-status-warning-border bg-status-warning-background text-status-warning-foreground hover:bg-status-warning-background dark:border-status-warning-border dark:bg-status-warning-background dark:text-status-warning-foreground',
  }
}

function getPriceAdjustmentIndexBadgeLabel(adjustment) {
  if (adjustment?.amount !== undefined && adjustment?.amount !== null) {
    return getPriceAdjustmentSignedAmountLabel(adjustment)
  }

  return adjustment?.summary ?? 'Active'
}

function getPriceAdjustmentIndexEndLabel(adjustment) {
  const activeWindowEnd = adjustment?.active_window?.ends_at ?? adjustment?.active_window_ends_at

  if (activeWindowEnd) {
    const parsedEnd = dayjs(activeWindowEnd)

    if (parsedEnd.isValid()) {
      return `ends ${parsedEnd.subtract(1, 'day').format('M/D')}`
    }
  }

  return null
}

function getPriceAdjustmentIndexTooltipScheduleLabel(adjustment) {
  const endLabel = getPriceAdjustmentIndexEndLabel(adjustment)

  if (endLabel) {
    return endLabel
  }

  const scheduleSummary = getPriceAdjustmentScheduleSummary(adjustment)

  if (typeof scheduleSummary === 'string' && scheduleSummary.trim()) {
    return scheduleSummary.replace(/^Active now\.\s*/i, '').replace(/\.$/, '')
  }

  return 'Active now'
}

function CollectionVisibilityMarker({ collection }) {
  const isPublished = collection?.is_published === true
  const Icon = isPublished ? Check : EyeOff

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-xs font-medium',
        isPublished ? 'text-muted-foreground' : 'text-warning'
      )}
    >
      <Icon
        className={cn('size-3.5', isPublished ? 'text-success/70' : 'text-warning')}
        aria-hidden="true"
      />
      {isPublished ? 'Visible' : 'Hidden'}
    </span>
  )
}

function collectionCountText(total) {
  return `${total} collection${total === 1 ? '' : 's'}`
}

function CollectionStatusFilterSelect({ value, onValueChange }) {
  const isFiltered = value !== 'all'

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        aria-label="Filter collections by website visibility"
        selected={isFiltered}
        className="w-fit"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {collectionPublishStatusOptions.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.icon}
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function CollectionTypeFilterSelect({ value, onValueChange }) {
  const isFiltered = value !== 'all'

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        aria-label="Filter collections by type"
        selected={isFiltered}
        className="w-fit"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {collectionTypeFilterOptions.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.icon}
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function getCollectionsFromResponse(response) {
  return Array.isArray(response) ? response : (response?.data ?? [])
}

function PriceChangeScopeBadge({
  adjustment,
  scopeLabel,
  variantClassName,
  path,
  canEditPriceChanges,
}) {
  const badgeLabel = getPriceAdjustmentIndexBadgeLabel(adjustment)
  const endLabel = getPriceAdjustmentIndexEndLabel(adjustment)
  const scheduleLabel = getPriceAdjustmentIndexTooltipScheduleLabel(adjustment)
  const badge = (
    <Badge variant="outline" size="xs" className={cn('gap-1.5', variantClassName)}>
      <Tags className="size-3" aria-hidden="true" />
      <span>{badgeLabel}</span>
      {endLabel ? (
        <>
          <span className="text-muted-foreground" aria-hidden="true">
            &middot;
          </span>
          <span>{endLabel}</span>
        </>
      ) : null}
    </Badge>
  )
  const content = (
    <span className="inline-flex flex-col items-center gap-1">
      {badge}
      <span className="whitespace-nowrap text-xs text-muted-foreground">{scopeLabel}</span>
    </span>
  )

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {canEditPriceChanges ? (
          <Link
            {...path.priceChanges.show(adjustment.id).link}
            className="relative z-20 inline-flex"
            aria-label={`${badgeLabel}${endLabel ? ` ${endLabel}` : ''} ${scopeLabel} price change`}
          >
            {content}
          </Link>
        ) : (
          <span className="relative z-20 inline-flex">{content}</span>
        )}
      </TooltipTrigger>
      <TooltipContent>
        <div className="space-y-1">
          <div>{getPriceAdjustmentActionLabel(adjustment)}</div>
          <div>{scheduleLabel}.</div>
          <div>{scopeLabel} price change active right now.</div>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

function ActiveCollectionPriceChangeCell({ collection, path, canEditPriceChanges }) {
  if (isSubscriptionPackageCollection(collection)) {
    return (
      <Typography variant="supporting" italic className="whitespace-nowrap">
        Not applicable
      </Typography>
    )
  }

  const adjustment = getCollectionActivePriceAdjustment(collection)

  if (!adjustment?.id) {
    return (
      <Typography variant="supporting" italic className="whitespace-nowrap">
        No active change
      </Typography>
    )
  }

  const { scopeLabel, variantClassName } = getPriceAdjustmentBadgeMeta(adjustment)

  return (
    <div className="flex items-center justify-center">
      <PriceChangeScopeBadge
        adjustment={adjustment}
        scopeLabel={scopeLabel}
        variantClassName={variantClassName}
        path={path}
        canEditPriceChanges={canEditPriceChanges}
      />
    </div>
  )
}

function getCollectionIndexColumns({
  path,
  canEditPriceChanges,
  canViewImportedShops,
  canViewPriceChanges,
}) {
  return [
    {
      id: 'collection',
      accessorFn: (collection) => getCollectionName(collection).toLowerCase(),
      header: 'Collection',
      cell: ({ row }) => {
        const collection = row.original

        return (
          <div className="flex min-w-0 items-center gap-3">
            <ImageThumbnail
              media={collection.media?.[0]}
              className="h-14 w-14 rounded-xl shadow-none"
            />
            <div className="min-w-0">
              <Typography
                variant="body"
                weight="semibold"
                interaction="hoverUnderline"
                className="block line-clamp-2 [overflow-wrap:anywhere]"
              >
                {collection.name}
              </Typography>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <CollectionVisibilityMarker collection={collection} />
              </div>
            </div>
          </div>
        )
      },
      meta: {
        label: 'Collection',
        kind: 'primary',
        icon: LibraryBig,
        sortable: collectionIndexSortColumns.collection,
        width: '24rem',
        minWidth: '17rem',
      },
    },
    {
      id: 'type',
      accessorFn: (collection) => getCollectionOriginBadgeMeta(collection).label,
      header: 'Type',
      cell: ({ row }) => {
        return (
          <CollectionOriginBadge
            collection={row.original}
            className={getCollectionOriginBadgeToneClassName(row.original)}
          />
        )
      },
      meta: {
        label: 'Type',
        kind: 'badge',
        link: false,
        align: 'center',
        sortable: collectionIndexSortColumns.type,
        width: '7.5rem',
        minWidth: '6.5rem',
      },
    },
    {
      id: 'product-selection',
      accessorFn: (collection) => getCollectionMembershipBadgeMeta({ collection }).label,
      header: 'How It Fills',
      cell: ({ row }) => (
        <CollectionMembershipBadge
          collection={row.original}
          className={getCollectionMembershipBadgeToneClassName({ collection: row.original })}
        />
      ),
      meta: {
        label: 'How It Fills',
        kind: 'badge',
        link: false,
        align: 'center',
        width: '9rem',
        minWidth: '8rem',
      },
    },
    {
      id: 'products',
      accessorFn: getProductCount,
      header: 'Products',
      cell: ({ row }) => {
        const productCount = getProductCount(row.original)
        const visibleProductCount = getVisibleProductCount(row.original)
        const hasVisibilityDifference = visibleProductCount !== productCount

        return (
          <div className="space-y-0.5 text-center">
            <span className="block whitespace-nowrap font-semibold tabular-nums">
              {productCount}
            </span>
            {hasVisibilityDifference ? (
              <Typography variant="label" tone="warning" className="block whitespace-nowrap">
                {visibleProductCount} shown
              </Typography>
            ) : null}
          </div>
        )
      },
      meta: {
        label: 'Products',
        kind: 'number',
        align: 'center',
        sortable: collectionIndexSortColumns.products,
        width: '7.5rem',
      },
    },
    canViewPriceChanges
      ? {
          id: 'price-change',
          accessorFn: (collection) => getCollectionActivePriceAdjustment(collection)?.id ?? null,
          header: 'Price Change',
          cell: ({ row }) => (
            <ActiveCollectionPriceChangeCell
              collection={row.original}
              path={path}
              canEditPriceChanges={canEditPriceChanges}
            />
          ),
          meta: {
            label: 'Price Change',
            kind: 'badge',
            link: false,
            align: 'center',
            sortable: collectionIndexSortColumns.priceChange,
            width: '10rem',
          },
        }
      : null,
    {
      id: 'last-changed',
      accessorFn: getLastChangedAt,
      header: 'Last Changed',
      cell: ({ row }) => (
        <span className="whitespace-nowrap tabular-nums">
          {formatLastChangedAt(getLastChangedAt(row.original))}
        </span>
      ),
      meta: {
        label: 'Last Changed',
        kind: 'date',
        icon: CalendarClock,
        align: 'center',
        sortable: collectionIndexSortColumns.lastChanged,
        width: '8rem',
        minWidth: '7rem',
      },
    },
    canViewImportedShops
      ? {
          id: 'imported-shops',
          accessorFn: getImportedShopCount,
          header: 'Imported Shops',
          cell: ({ row }) => (
            <span className="whitespace-nowrap font-semibold tabular-nums">
              {getImportedShopCount(row.original)}
            </span>
          ),
          meta: {
            label: 'Imported Shops',
            kind: 'number',
            icon: Store,
            align: 'center',
            sortable: collectionIndexSortColumns.importedShops,
            width: '9rem',
            minWidth: '7rem',
          },
        }
      : null,
  ].filter(Boolean)
}

export const Route = createFileRoute('/_authenticated/$orgId/products/collections/')({
  component: CollectionsIndexPage,
  validateSearch: (search) => collectionListSearchSchema.parse(search),
  search: {
    middlewares: [stripSearchParams(strippedCollectionListSearchParams)],
  },
  loaderDeps: ({ search }) => ({ search: getCollectionListSearchParams(search) }),
  loader: async ({ context, deps: { search }, params }) => {
    if (!context.ability.can('list', 'collections')) throw notFound()

    if (search.type === 'add-on-collections') {
      throw redirect({
        to: '/$orgId/products/add-on-sets',
        params: { orgId: params.orgId },
        replace: true,
      })
    }

    const filters = collectionIndexFilters({
      includeImportedShopsCount: isGlobalOrganizationId(params.orgId),
      search,
    })
    const shouldPrefetchPriceAdjustments =
      context.ability.can('edit', 'products') &&
      supportsPriceAdjustmentsForOrganizationId(params.orgId)

    void prefetchIndexTableRouteQueries(context.queryClient, [
      collectionQueries.indexTable({ organizationId: params.orgId, filters }),
      shouldPrefetchPriceAdjustments
        ? priceAdjustmentQueries.index({
            organizationId: params.orgId,
            filters: priceAdjustmentFilters,
          })
        : null,
    ])

    return { isReadOnly: !context.ability.can('edit', 'collections') }
  },
})

export function CollectionsIndexPage() {
  const { orgId } = useParams({ strict: false })
  const navigate = useNavigate()
  const isGlobalOrg = useIsGlobalOrg()
  const urlSearch = Route.useSearch()
  const selectedPublishStatus =
    urlSearch.publish_status ?? defaultCollectionListSearchParams.publish_status
  const path = usePathHelper()
  const { data: organization } = useOrganization(orgId)
  const ability = useAbility()
  const canEditPriceChanges = ability.can('edit', 'products') && !isGlobalOrg
  const canViewImportedShops = isGlobalOrg
  const canViewPriceChanges = !isGlobalOrg
  const filters = useMemo(
    () =>
      collectionIndexFilters({
        includeImportedShopsCount: isGlobalOrg,
        search: urlSearch,
      }),
    [isGlobalOrg, urlSearch]
  )
  const { updatePage, updatePageSize, updateSearchTerm, updateSort, updateTableParams } =
    useIndexTableSearchParams({
      navigate,
      getSearchParams: getCollectionListSearchParams,
      searchTerm: urlSearch.search ?? '',
    })
  const hasActiveSearch =
    (urlSearch.search ?? '').trim().length > 0 ||
    urlSearch.publish_status !== defaultCollectionListSearchParams.publish_status ||
    urlSearch.collection_type !== defaultCollectionListSearchParams.collection_type
  const { data: collectionsResponse, isFetching, isPending } = useCollectionsTable({ filters })
  const collections = getCollectionsFromResponse(collectionsResponse)
  const paginationMeta = getIndexPaginationMeta(
    collectionsResponse,
    urlSearch.page,
    urlSearch.page_size
  )
  const columns = useMemo(
    () =>
      getCollectionIndexColumns({
        path,
        canEditPriceChanges,
        canViewImportedShops,
        canViewPriceChanges,
      }),
    [path, canEditPriceChanges, canViewImportedShops, canViewPriceChanges]
  )
  const [isFsnImportDialogOpen, setIsFsnImportDialogOpen] = useState(false)
  const [isFsnImportPending, setIsFsnImportPending] = useState(false)
  const [activeFsnImportAttemptId, setActiveFsnImportAttemptId] = useState(null)
  const [createdFsnImportAttempt, setCreatedFsnImportAttempt] = useState(null)
  const handledFsnImportAttemptIdsRef = useRef(new Set())
  const { mutateAsync: importFsnCatalog } = useImportGlobalCollectionSourceMutation()
  const activeCollectionImportAttemptQuery = useActiveGlobalCollectionImportAttemptQuery({
    enabled:
      ENABLE_FSN_CATALOG_IMPORT_ACTION && organization?.type !== OrganizationType.Global.Value,
  })
  const refetchActiveCollectionImportAttempt = activeCollectionImportAttemptQuery.refetch
  const recoveredFsnImportAttempt =
    activeCollectionImportAttemptQuery.data?.type === 'global_collection_source'
      ? activeCollectionImportAttemptQuery.data
      : null
  const fsnImportAttemptQuery = useGlobalCollectionImportAttemptQuery(activeFsnImportAttemptId, {
    enabled: Boolean(activeFsnImportAttemptId),
  })
  const createdFsnCatalogImportAttempt =
    createdFsnImportAttempt?.type === 'global_collection_source' ? createdFsnImportAttempt : null
  const displayedFsnImportAttempt =
    fsnImportAttemptQuery.data ?? recoveredFsnImportAttempt ?? createdFsnCatalogImportAttempt
  const isFsnImportRunning =
    isFsnImportPending ||
    displayedFsnImportAttempt?.status === 'queued' ||
    displayedFsnImportAttempt?.status === 'running'

  useEffect(() => {
    if (
      !recoveredFsnImportAttempt?.id ||
      activeFsnImportAttemptId === recoveredFsnImportAttempt.id
    ) {
      return
    }

    // oxlint-disable-next-line react/set-state-in-effect -- grandfathered
    setActiveFsnImportAttemptId(recoveredFsnImportAttempt.id)
  }, [activeFsnImportAttemptId, recoveredFsnImportAttempt])

  useEffect(() => {
    if (
      !displayedFsnImportAttempt ||
      handledFsnImportAttemptIdsRef.current.has(displayedFsnImportAttempt.id)
    ) {
      return
    }

    if (displayedFsnImportAttempt.status === 'completed') {
      handledFsnImportAttemptIdsRef.current.add(displayedFsnImportAttempt.id)
      void invalidateCollectionImportDependencies(orgId)
      void refetchActiveCollectionImportAttempt?.()
      toast.success('FSN catalog import finished')

      return
    }

    if (displayedFsnImportAttempt.status === 'failed') {
      handledFsnImportAttemptIdsRef.current.add(displayedFsnImportAttempt.id)
      void refetchActiveCollectionImportAttempt?.()
      toast.error('FSN catalog import failed', {
        description:
          displayedFsnImportAttempt.error?.message ??
          displayedFsnImportAttempt.message ??
          'The catalog import failed.',
      })
    }
  }, [displayedFsnImportAttempt, orgId, refetchActiveCollectionImportAttempt])

  const handlePageIntent = (page) => {
    void queryClient.prefetchQuery(
      collectionQueries.indexTable({
        organizationId: orgId,
        filters: {
          ...filters,
          page,
        },
      })
    )
  }

  const handlePublishStatusChange = (publishStatus) =>
    updateTableParams({ publish_status: publishStatus })

  const handleCollectionTypeFilterChange = (collectionType) =>
    updateTableParams({ collection_type: collectionType })

  const clearCollectionFilters = () =>
    updateTableParams({
      search: undefined,
      publish_status: defaultCollectionListSearchParams.publish_status,
      collection_type: defaultCollectionListSearchParams.collection_type,
    })

  const handleImportFsnCatalog = async () => {
    try {
      setIsFsnImportPending(true)
      setCreatedFsnImportAttempt(null)
      const attempt = await importFsnCatalog({ sourceSystem: 'fsn' })
      setActiveFsnImportAttemptId(attempt.id)
      setCreatedFsnImportAttempt(attempt)
      toast.success('FSN catalog import started')
    } catch (error) {
      setCreatedFsnImportAttempt(null)
      toast.error('Failed to start FSN catalog import', {
        description: getErrorMessage(error),
      })
    } finally {
      setIsFsnImportDialogOpen(false)
      setIsFsnImportPending(false)
    }
  }

  return (
    <div className="w-full max-w-none space-y-4">
      <ProductCatalogNav active="collections" />
      <AdminPageHeader
        icon={LibraryBig}
        title="Collections"
        description="Use collections to group products on your storefront for holidays, occasions, and featured sections."
        actions={
          <>
            {organization?.type !== OrganizationType.Global.Value ? (
              <Can do="create" on="collections">
                <>
                  <AdminHeaderActionButton
                    variant="outline"
                    className={collectionHeaderActionClassName}
                    asChild
                  >
                    <Link {...path.collections.link('import')}>
                      <Import />
                      Import Collections
                    </Link>
                  </AdminHeaderActionButton>
                  {ENABLE_FSN_CATALOG_IMPORT_ACTION ? (
                    <ConfirmationDialog
                      open={isFsnImportDialogOpen}
                      onOpenChange={setIsFsnImportDialogOpen}
                      isPending={isFsnImportRunning}
                      variant="question"
                      title="Import FSN Catalog"
                      description="Start a background import for all visible FSN collections and products. Refresh this page after it finishes to see the new catalog."
                      confirmText="Start Import"
                      pendingText="Starting..."
                      trigger={
                        <AdminHeaderActionButton
                          variant="outline"
                          className={collectionHeaderActionClassName}
                          disabled={isFsnImportRunning}
                        >
                          <Import />
                          {isFsnImportRunning ? 'Import Running' : 'Import FSN Catalog'}
                        </AdminHeaderActionButton>
                      }
                      onConfirm={handleImportFsnCatalog}
                    />
                  ) : null}
                </>
              </Can>
            ) : null}
            <Can do="create" on="collections">
              <CreateActionButton className={collectionHeaderActionClassName} asChild>
                <Link {...path.collections.link('create')}>Create New Collection</Link>
              </CreateActionButton>
            </Can>
          </>
        }
      />
      {displayedFsnImportAttempt ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/80 bg-muted/15 px-4 py-3">
          <div>
            <Typography variant="labelStrong">FSN catalog import</Typography>
            <Typography variant="supporting">
              {displayedFsnImportAttempt.message ?? displayedFsnImportAttempt.stage_label}
            </Typography>
          </div>
          <Badge
            variant={
              displayedFsnImportAttempt.status === 'completed'
                ? 'success'
                : displayedFsnImportAttempt.status === 'failed'
                  ? 'destructive'
                  : 'info'
            }
          >
            {displayedFsnImportAttempt.stage_label}
          </Badge>
        </div>
      ) : null}
      {canEditPriceChanges ? (
        <PriceChangeAwareCollectionsTable
          collections={collections}
          columns={columns}
          path={path}
          search={urlSearch.search ?? ''}
          publishStatus={selectedPublishStatus}
          collectionTypeFilter={urlSearch.collection_type}
          hasActiveSearch={hasActiveSearch}
          onPublishStatusChange={handlePublishStatusChange}
          onCollectionTypeChange={handleCollectionTypeFilterChange}
          onClearFilters={clearCollectionFilters}
          onSearchCommit={updateSearchTerm}
          sort={urlSearch.sort}
          onSortChange={updateSort}
          paginationMeta={paginationMeta}
          pageSize={urlSearch.page_size}
          onPageChange={updatePage}
          onPageSizeChange={updatePageSize}
          onPageIntent={handlePageIntent}
          isFetching={isFetching}
          isPending={isPending}
        />
      ) : (
        <CollectionsIndexTable
          collections={collections}
          columns={columns}
          path={path}
          search={urlSearch.search ?? ''}
          publishStatus={selectedPublishStatus}
          collectionTypeFilter={urlSearch.collection_type}
          hasActiveSearch={hasActiveSearch}
          onPublishStatusChange={handlePublishStatusChange}
          onCollectionTypeChange={handleCollectionTypeFilterChange}
          onClearFilters={clearCollectionFilters}
          onSearchCommit={updateSearchTerm}
          sort={urlSearch.sort}
          onSortChange={updateSort}
          paginationMeta={paginationMeta}
          pageSize={urlSearch.page_size}
          onPageChange={updatePage}
          onPageSizeChange={updatePageSize}
          onPageIntent={handlePageIntent}
          isFetching={isFetching}
          isPending={isPending}
        />
      )}
    </div>
  )
}

function PriceChangeAwareCollectionsTable({
  collections,
  columns,
  path,
  search,
  publishStatus,
  collectionTypeFilter,
  hasActiveSearch,
  onPublishStatusChange,
  onCollectionTypeChange,
  onClearFilters,
  onSearchCommit,
  sort,
  onSortChange,
  paginationMeta,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onPageIntent,
  isFetching,
  isPending,
}) {
  const { data: priceAdjustments } = usePriceAdjustments()
  const activeGlobalPriceChange = getActiveGlobalPriceChange(priceAdjustments)

  return (
    <>
      <ActiveGlobalPriceChangeBanner adjustment={activeGlobalPriceChange} />
      <CollectionsIndexTable
        collections={collections}
        columns={columns}
        path={path}
        search={search}
        publishStatus={publishStatus}
        collectionTypeFilter={collectionTypeFilter}
        hasActiveSearch={hasActiveSearch}
        onPublishStatusChange={onPublishStatusChange}
        onCollectionTypeChange={onCollectionTypeChange}
        onClearFilters={onClearFilters}
        onSearchCommit={onSearchCommit}
        sort={sort}
        onSortChange={onSortChange}
        paginationMeta={paginationMeta}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onPageIntent={onPageIntent}
        isFetching={isFetching}
        isPending={isPending}
      />
    </>
  )
}

function CollectionsIndexTable({
  collections,
  columns,
  path,
  search,
  publishStatus,
  collectionTypeFilter,
  hasActiveSearch,
  onPublishStatusChange,
  onCollectionTypeChange,
  onClearFilters,
  onSearchCommit,
  sort,
  onSortChange,
  paginationMeta,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onPageIntent,
  isFetching,
  isPending,
}) {
  return (
    <div className="space-y-4">
      <IndexDataTable
        data={collections}
        columns={columns}
        sort={sort}
        onSortChange={onSortChange}
        rowLink={(collection) => path.collections.show(collection.id).link}
        getRowId={(collection) => String(collection.id)}
        isLoading={isPending}
        toolbar={
          <IndexTableToolbar
            primary={
              <IndexTableSearchBox
                value={search}
                onCommit={onSearchCommit}
                countText={collectionCountText(paginationMeta.total)}
                isFetching={isFetching}
                placeholder="Search collections..."
              />
            }
            secondary={
              <>
                {hasActiveSearch ? <IndexTableClearFiltersButton onClick={onClearFilters} /> : null}
                <CollectionStatusFilterSelect
                  value={publishStatus}
                  onValueChange={onPublishStatusChange}
                />
                <CollectionTypeFilterSelect
                  value={collectionTypeFilter}
                  onValueChange={onCollectionTypeChange}
                />
              </>
            }
          />
        }
        emptyState={
          <Typography variant="supporting">
            {search ? 'No matching collections.' : 'No collections found.'}
          </Typography>
        }
        tableClassName="text-sm"
      />
      <IndexTablePagination
        meta={paginationMeta}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageIntent={onPageIntent}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  )
}

function ActiveGlobalPriceChangeBanner({ adjustment }) {
  const path = usePathHelper()

  if (!adjustment) {
    return null
  }

  return (
    <PriceChangeAlert
      title="Global price change active right now"
      className="mb-4"
      action={
        <InfoNoticeAction {...path.priceChanges.show(adjustment.id).link}>
          View global price change
          <ChevronRight className="size-3.5" />
        </InfoNoticeAction>
      }
    >
      <>
        <Typography variant="label" className="block">
          {getPriceAdjustmentActionLabel(adjustment)} for all products.
        </Typography>
        <Typography variant="label" className="block">
          {getPriceAdjustmentScheduleSummary(adjustment)}
        </Typography>
      </>
    </PriceChangeAlert>
  )
}
